import { TRPCClientError } from '@trpc/client';

/** 서버(tRPC errorFormatter)가 내려준 필드별 오류 묶음. */
export type ServerFieldErrors = Readonly<Record<string, readonly string[]>>;

/**
 * tRPC 오류에서 서버 검증의 필드별 오류를 꺼낸다. 없으면 null.
 *
 * 클라이언트가 같은 Zod 스키마로 선검증하므로 드물지만, 스키마 버전이 어긋난
 * 배포 창 등에서 서버 검증만 실패할 수 있다 — 그 오류를 버리지 않고 폼에 매핑한다.
 */
export const getServerFieldErrors = (error: unknown): ServerFieldErrors | null => {
  if (!(error instanceof TRPCClientError)) {
    return null;
  }

  const data = error.data as { fieldErrors?: ServerFieldErrors | null } | undefined;
  return data?.fieldErrors ?? null;
};

/**
 * 필드별 오류 목록에서 각 필드의 첫 메시지만 고른다.
 *
 * keys 로 대상 필드를 한정해, 폼마다 매핑을 하드코딩하지 않고 재사용한다. 서버 필드별 오류(ServerFieldErrors)는
 * 키가 정해지지 않은 묶음이라 그대로도 받는다 — keys 가 제네릭인 호출부(mapServerValidationErrors)에서도 넘길 수 있게.
 */
export const firstErrorPerField = <TKey extends string>(
  errors: Partial<Record<TKey, readonly string[] | undefined>> | ServerFieldErrors | null,
  keys: readonly TKey[],
): Partial<Record<TKey, string>> => {
  const result: Partial<Record<TKey, string>> = {};

  if (!errors) {
    return result;
  }

  for (const key of keys) {
    const first = errors[key]?.[0];

    if (first) {
      result[key] = first;
    }
  }

  return result;
};

/** 서버 입력 검증 오류를 폼에 옮긴 결과. */
export interface MappedServerValidationErrors<TKey extends string> {
  readonly fieldErrors: Partial<Record<TKey, string>>;
  /** 폼 칸으로 옮긴 오류가 하나도 없을 때만 채운다. */
  readonly formError: string | null;
}

/** 폼 칸으로 옮길 오류도, 보일 한국어 서버 문구도 없을 때의 폼 오류. */
const SERVER_VALIDATION_FALLBACK = '입력값을 확인해 주세요.';

const HANGUL = /[가-힣]/;

/**
 * 서버 입력 검증(필드별 오류가 실린 BAD_REQUEST)이면 폼에 옮길 오류를, 아니면 null 을 돌려준다.
 *
 * keys 로 옮긴 칸 오류가 하나도 없으면 — 폼이 모르는 항목(가입의 agreements 묶음 등)만 거부됐거나 필드별 오류가
 * 비었으면 — 서버 문구 가운데 첫 한국어 문구를, 없으면 고정 문구를 폼 오류로 채운다. 그러지 않으면 제출이 화면에
 * 아무 오류 없이 끝난다. 오류의 message 는 쓰지 않는다 — 입력 검증 오류의 message 는 ZodError 의 JSON 원문이다.
 */
export const mapServerValidationErrors = <TKey extends string>(
  error: unknown,
  keys: readonly TKey[],
): MappedServerValidationErrors<TKey> | null => {
  const serverFieldErrors = getServerFieldErrors(error);

  if (!serverFieldErrors) {
    return null;
  }

  const fieldErrors = firstErrorPerField(serverFieldErrors, keys);

  if (Object.keys(fieldErrors).length > 0) {
    return { fieldErrors, formError: null };
  }

  const koreanMessage = Object.values(serverFieldErrors)
    .flat()
    .find((message) => HANGUL.test(message));

  return { fieldErrors, formError: koreanMessage ?? SERVER_VALIDATION_FALLBACK };
};

/** 사용자에게 보여줄 오류 메시지. Error 가 아니면 기본 문구로 대체한다. */
export const getErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;
